import { useEffect, useState } from 'react';
import { useSearch } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff, ArrowRight, User } from 'lucide-react';

import { loginSchema, type LoginFormValues } from '@/features/login/types/auth';
import { loadSavedLogin, persistSavedLogin } from '@/features/login/lib/saved-login';
import { useLoginFlowStore } from '@/features/login/store/login-flow-store';
import { useLoginFlow } from '@/features/login/hooks/use-login-flow';
import { startIdpIntent, ZitadelApiError } from '@/shared/lib/zitadel-client';
import { Button } from '@/shared/ui/button';
import { Checkbox } from '@/shared/ui/checkbox';
import { Input } from '@/shared/ui/input';
import { Field } from '@/shared/ui/field';
import { Alert } from '@/shared/ui/alert';
import { AuthStage } from '@/shared/ui/auth-stage';
import { Separator } from '@/shared/ui/separator';
import { RequestAccessModal } from '@/features/request-access/components/request-access-modal';

const MICROSOFT_IDP_ID: string | undefined =
  import.meta.env.VITE_ZITADEL_MICROSOFT_IDP_ID || undefined;

function MicrosoftLogo({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 21 21"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}

export function LoginPage() {
  const search = useSearch({ from: '/login' });
  // Selector granular — evita recriar o objeto inteiro do store a cada render,
  // o que causaria um loop infinito no useEffect abaixo.
  const setAuthRequestId = useLoginFlowStore((s) => s.setAuthRequestId);
  const { status, errorMessage, submit } = useLoginFlow();
  const [showPassword, setShowPassword] = useState(false);
  const [requestOpen, setRequestOpen] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(false);
  const [idpStatus, setIdpStatus] = useState<'idle' | 'loading'>('idle');
  const [idpError, setIdpError] = useState<string | null>(null);

  // Persiste o authRequestId no store assim que a página carrega.
  // `search.authRequestId` já é `string | undefined` graças ao validateSearch da rota.
  useEffect(() => {
    if (search.authRequestId) setAuthRequestId(search.authRequestId);
  }, [search.authRequestId, setAuthRequestId]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { loginName: '', password: '' },
  });

  useEffect(() => {
    void loadSavedLogin().then((saved) => {
      if (!saved) return;
      setRememberDevice(true);
      reset({ loginName: saved.loginName, password: saved.password });
    });
  }, [reset]);

  const isLoading = status === 'loading';

  const onSubmit = async (values: LoginFormValues) => {
    await persistSavedLogin({
      remember: rememberDevice,
      loginName: values.loginName,
      password: values.password,
    });
    await submit(values);
  };

  const onMicrosoftLogin = async () => {
    if (!MICROSOFT_IDP_ID) return;
    if (!search.authRequestId) {
      setIdpError(
        'Parâmetro de autenticação ausente. Tente acessar novamente pelo aplicativo.',
      );
      return;
    }

    setIdpStatus('loading');
    setIdpError(null);
    try {
      const successUrl = `${window.location.origin}/idp-callback?authRequestId=${encodeURIComponent(search.authRequestId)}`;
      const failureUrl = `${window.location.origin}/login?authRequestId=${encodeURIComponent(search.authRequestId)}`;
      const { authUrl } = await startIdpIntent(MICROSOFT_IDP_ID, successUrl, failureUrl);
      window.location.assign(authUrl);
    } catch (err) {
      setIdpStatus('idle');
      if (err instanceof ZitadelApiError) {
        setIdpError(
          'Não foi possível iniciar o login com Microsoft. Tente novamente em instantes.',
        );
      } else {
        setIdpError('Não foi possível conectar ao servidor. Verifique sua conexão.');
      }
    }
  };

  return (
    <AuthStage>
      <h1
        className="whitespace-nowrap font-editorial font-light leading-[1.05] tracking-[-0.03em] text-ink-1"
        style={{ fontSize: 'clamp(1.9rem, 2.8vw, 2.8rem)' }}
      >
        Bem-vindo <em className="font-light italic text-brand">de volta.</em>
      </h1>
      <p
        className="mb-9 mt-2.5 font-medium tracking-[0.005em] text-brand"
        style={{ fontSize: 'clamp(15px, 1.15vw, 18px)' }}
      >
        Plataforma TKWS
      </p>

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-[18px]">
        {(errorMessage || idpError) && (
          <Alert variant="error" message={errorMessage ?? idpError ?? ''} />
        )}

        <Field
          label="Email corporativo"
          htmlFor="loginName"
          error={errors.loginName?.message}
        >
          <div className="relative">
            <Input
              id="loginName"
              type="text"
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              placeholder="seumail@groupws.com.br"
              error={!!errors.loginName}
              disabled={isLoading}
              className="h-[52px] pr-11 text-[15px]"
              {...register('loginName')}
            />
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3.5 text-ink-4">
              <User className="h-[18px] w-[18px]" strokeWidth={1.8} />
            </span>
          </div>
        </Field>

        <Field label="Senha" htmlFor="password" error={errors.password?.message}>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="••••••••"
              error={!!errors.password}
              disabled={isLoading}
              className="h-[52px] pr-11 text-[15px]"
              {...register('password')}
            />
            <button
              type="button"
              tabIndex={-1}
              aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
              onClick={() => setShowPassword((v) => !v)}
              className="absolute inset-y-0 right-0 flex items-center px-3.5 text-ink-4 transition-colors hover:text-ink-2"
            >
              {showPassword ? (
                <EyeOff className="h-[18px] w-[18px]" strokeWidth={1.8} />
              ) : (
                <Eye className="h-[18px] w-[18px]" strokeWidth={1.8} />
              )}
            </button>
          </div>
        </Field>

        <div className="flex items-center justify-between gap-3 pt-0.5">
          <label
            htmlFor="rememberDevice"
            className="flex cursor-pointer items-center gap-2.5"
          >
            <Checkbox
              id="rememberDevice"
              checked={rememberDevice}
              onCheckedChange={(checked) => setRememberDevice(checked === true)}
              disabled={isLoading}
            />
            <span className="text-[13px] text-ink-2">Lembrar deste dispositivo</span>
          </label>
          <a
            href="/recovery"
            className="shrink-0 text-[13px] font-semibold text-brand underline-offset-[3px] hover:underline"
          >
            Esqueci minha senha
          </a>
        </div>

        <div className="pt-1.5">
          <Button
            type="submit"
            size="lg"
            loading={isLoading}
            className="h-[56px] text-[15px]"
          >
            {isLoading ? 'Entrando…' : 'Entrar'}
            {!isLoading && <ArrowRight className="h-[18px] w-[18px]" />}
          </Button>
        </div>

        {MICROSOFT_IDP_ID && (
          <div className="space-y-[18px] pt-1.5">
            <Separator label="Ou" />
            <Button
              type="button"
              size="lg"
              loading={idpStatus === 'loading'}
              disabled={isLoading}
              onClick={() => {
                void onMicrosoftLogin();
              }}
              className="h-[56px] border-transparent bg-white text-[15px] text-[#1B2A3D] hover:bg-white/90 active:bg-white/80"
            >
              {idpStatus !== 'loading' && <MicrosoftLogo className="h-[18px] w-[18px]" />}
              {idpStatus === 'loading' ? 'Redirecionando…' : 'Entrar com Microsoft'}
            </Button>
          </div>
        )}
      </form>

      <p className="mt-8 text-center text-[13px] text-ink-4">
        Ainda não tem conta?{' '}
        <button
          type="button"
          onClick={() => setRequestOpen(true)}
          className="font-semibold text-brand underline-offset-[3px] hover:underline"
        >
          Solicitar convite!
        </button>
      </p>

      <RequestAccessModal open={requestOpen} onClose={() => setRequestOpen(false)} />
    </AuthStage>
  );
}
